import {LobbyImage} from '@colonist/types'
import {TranslationArrayKeys, TranslationObject} from '@colonist/i18n'
import {BotsMatchType, MatchmakingMatchTypes, MatchType} from '@colonist/game-base-types'
import {createDivIn, createImgIn, createListItemIn, createParagraphIn, createUnorderedListIn, setElementInnerHTML, setElementInnerText, setElementOnClick} from '../../ui_utils/DomUtils'
import {ClientUserState} from '../../ClientUserState'
import {createShuffleQueueIconStack} from '../../matchmaking/ShuffleQueueUtils'
import {getMatchTypePlayTime, getMatchTypePlayerCount, startBotGame} from '../../matchmaking/MatchmakingUtilsClient'
import {clientI18n} from '../../ClientI18n'

interface ModeDetailsData {
    icon: LobbyImage
    title: TranslationObject
    time: number | undefined
    playerCount: number | undefined
    about: TranslationObject
    rules: TranslationArrayKeys
    faq: TranslationArrayKeys
}

export type MatchmakingEntryCallback = (matchType: MatchmakingMatchTypes) => Promise<void>

export abstract class UIModeSelectorDetailsController {
    protected readonly detailsContainer: HTMLDivElement
    private readonly backButton: HTMLDivElement
    private readonly titleElement: HTMLHeadingElement
    private readonly contentContainer: HTMLDivElement
    private readonly iconContainer: HTMLDivElement
    private readonly infoElement: HTMLDivElement
    private readonly timeElement: HTMLParagraphElement
    private readonly playerCountElement: HTMLParagraphElement
    private readonly aboutElement: HTMLParagraphElement
    private readonly rulesListElement: HTMLDivElement
    private readonly faqListElement: HTMLDivElement
    private readonly playButton: HTMLButtonElement

    private currentMatchType: MatchType | undefined
    private readonly onEnterMatchmaking: MatchmakingEntryCallback

    private static modeDetails: Map<MatchType, ModeDetailsData>

    protected constructor(onEnterMatchmaking: MatchmakingEntryCallback) {
        this.onEnterMatchmaking = onEnterMatchmaking

        this.detailsContainer = document.getElementById('mm-details-container') as HTMLDivElement
        this.backButton = document.getElementById('mm-details-back-button') as HTMLDivElement
        this.titleElement = document.getElementById('mm-details-title') as HTMLHeadingElement
        this.contentContainer = document.getElementById('mm-details-content') as HTMLDivElement
        this.iconContainer = document.getElementById('mm-details-icon-container') as HTMLDivElement
        this.infoElement = document.getElementById('mm-details-info') as HTMLDivElement
        this.timeElement = document.getElementById('mm-details-time') as HTMLParagraphElement
        this.playerCountElement = document.getElementById('mm-details-player-count') as HTMLParagraphElement
        this.aboutElement = document.getElementById('mm-details-about') as HTMLParagraphElement
        this.rulesListElement = document.getElementById('mm-details-rules-list') as HTMLDivElement
        this.faqListElement = document.getElementById('mm-details-faq-list') as HTMLDivElement
        this.playButton = document.getElementById('mm-details-play-button') as HTMLButtonElement

        this.setupEventHandlers()
        void UIModeSelectorDetailsController.initModeDetails()

        ClientUserState.registerChangeListener(async() => {
            await UIModeSelectorDetailsController.initModeDetails()
            if(this.currentMatchType != undefined) this.populateDetails(this.currentMatchType)
        })
    }

    private static async initModeDetails(): Promise<void> {
        await ClientUserState.waitUntilFresh

        UIModeSelectorDetailsController.modeDetails = new Map<MatchType, ModeDetailsData>([
            [BotsMatchType.BeginnerMode, {
                icon: LobbyImage.BotBeginner,
                title: {key: 'strings:homePage.matchmakingSelect.cards.bots.beginner'},
                time: getMatchTypePlayTime(BotsMatchType.BeginnerMode),
                playerCount: getMatchTypePlayerCount(BotsMatchType.BeginnerMode),
                about: {key: 'strings:homePage.matchmakingSelect.detailsPage.beginner.about'},
                rules: 'strings:homePage.matchmakingSelect.detailsPage.beginner.rules',
                faq: 'strings:homePage.matchmakingSelect.detailsPage.beginner.faq',
            }],
            [BotsMatchType.Easy, {
                icon: LobbyImage.BotEasy,
                title: {key: 'strings:homePage.matchmakingSelect.cards.bots.easy'},
                time: getMatchTypePlayTime(BotsMatchType.Easy),
                playerCount: getMatchTypePlayerCount(BotsMatchType.Easy),
                about: {key: 'strings:homePage.matchmakingSelect.detailsPage.easy.about'},
                rules: 'strings:homePage.matchmakingSelect.detailsPage.easy.rules',
                faq: 'strings:homePage.matchmakingSelect.detailsPage.easy.faq',
            }],
            [BotsMatchType.Medium, {
                icon: LobbyImage.BotMedium,
                title: {key: 'strings:homePage.matchmakingSelect.cards.bots.medium'},
                time: getMatchTypePlayTime(BotsMatchType.Medium),
                playerCount: getMatchTypePlayerCount(BotsMatchType.Medium),
                about: {key: 'strings:homePage.matchmakingSelect.detailsPage.medium.about'},
                rules: 'strings:homePage.matchmakingSelect.detailsPage.medium.rules',
                faq: 'strings:homePage.matchmakingSelect.detailsPage.medium.faq',
            }],
            [BotsMatchType.Hard, {
                icon: LobbyImage.BotHard,
                title: {key: 'strings:homePage.matchmakingSelect.cards.bots.hard'},
                time: getMatchTypePlayTime(BotsMatchType.Hard),
                playerCount: getMatchTypePlayerCount(BotsMatchType.Hard),
                about: {key: 'strings:homePage.matchmakingSelect.detailsPage.hard.about'},
                rules: 'strings:homePage.matchmakingSelect.detailsPage.hard.rules',
                faq: 'strings:homePage.matchmakingSelect.detailsPage.hard.faq',
            }],
            [MatchmakingMatchTypes.UnrankedClassic1v1, {
                icon: LobbyImage.GameMode1v1,
                title: {key: 'strings:homePage.matchmakingSelect.cards.1v1'},
                time: getMatchTypePlayTime(MatchmakingMatchTypes.UnrankedClassic1v1),
                playerCount: getMatchTypePlayerCount(MatchmakingMatchTypes.UnrankedClassic1v1),
                about: {key: 'strings:homePage.matchmakingSelect.detailsPage.casual1v1.about'},
                rules: 'strings:homePage.matchmakingSelect.detailsPage.casual1v1.rules',
                faq: 'strings:homePage.matchmakingSelect.detailsPage.casual1v1.faq',
            }],
            [MatchmakingMatchTypes.UnrankedClassic4P, {
                icon: LobbyImage.GameModeBase,
                title: {key: 'strings:homePage.matchmakingSelect.cards.4Player'},
                time: getMatchTypePlayTime(MatchmakingMatchTypes.UnrankedClassic4P),
                playerCount: getMatchTypePlayerCount(MatchmakingMatchTypes.UnrankedClassic4P),
                about: {key: 'strings:homePage.matchmakingSelect.detailsPage.casual4Player.about'},
                rules: 'strings:homePage.matchmakingSelect.detailsPage.casual4Player.rules',
                faq: 'strings:homePage.matchmakingSelect.detailsPage.casual4Player.faq',
            }],
            [MatchmakingMatchTypes.UnrankedShuffle, {
                icon: LobbyImage.GameModeShuffle,
                title: {key: 'strings:homePage.unranked.shuffle'},
                time: getMatchTypePlayTime(MatchmakingMatchTypes.UnrankedShuffle),
                playerCount: getMatchTypePlayerCount(MatchmakingMatchTypes.UnrankedShuffle),
                about: {key: 'strings:homePage.matchmakingSelect.detailsPage.shuffle.about'},
                rules: 'strings:homePage.matchmakingSelect.detailsPage.shuffle.rules',
                faq: 'strings:homePage.matchmakingSelect.detailsPage.shuffle.faq',
            }],
            [MatchmakingMatchTypes.RankedClassic1v1, {
                icon: LobbyImage.GameModeRanked1v1,
                title: {key: 'strings:homePage.matchmakingSelect.cards.1v1'},
                time: getMatchTypePlayTime(MatchmakingMatchTypes.RankedClassic1v1),
                playerCount: getMatchTypePlayerCount(MatchmakingMatchTypes.RankedClassic1v1),
                about: {key: 'strings:homePage.matchmakingSelect.detailsPage.ranked1v1.about'},
                rules: 'strings:homePage.matchmakingSelect.detailsPage.ranked1v1.rules',
                faq: 'strings:homePage.matchmakingSelect.detailsPage.ranked1v1.faq',
            }],
            [MatchmakingMatchTypes.RankedClassic4P, {
                icon: LobbyImage.GameModeRanked4P,
                title: {key: 'strings:homePage.matchmakingSelect.cards.4Player'},
                time: getMatchTypePlayTime(MatchmakingMatchTypes.RankedClassic4P),
                playerCount: getMatchTypePlayerCount(MatchmakingMatchTypes.RankedClassic4P),
                about: {key: 'strings:homePage.matchmakingSelect.detailsPage.ranked4P.about'},
                rules: 'strings:homePage.matchmakingSelect.detailsPage.ranked4P.rules',
                faq: 'strings:homePage.matchmakingSelect.detailsPage.ranked4P.faq',
            }],
            [MatchmakingMatchTypes.RankedCitiesAndKnights4P, {
                icon: LobbyImage.GameModeCnK,
                title: {key: 'strings:homePage.matchmakingSelect.cards.citiesAndKnights'},
                time: getMatchTypePlayTime(MatchmakingMatchTypes.RankedCitiesAndKnights4P),
                playerCount: getMatchTypePlayerCount(MatchmakingMatchTypes.RankedCitiesAndKnights4P),
                about: {key: 'strings:homePage.matchmakingSelect.detailsPage.citiesAndKnights.about'},
                rules: 'strings:homePage.matchmakingSelect.detailsPage.citiesAndKnights.rules',
                faq: 'strings:homePage.matchmakingSelect.detailsPage.citiesAndKnights.faq',
            }],
        ])
    }

    show(matchType: MatchType) {
        this.currentMatchType = matchType
        this.populateDetails(matchType)
        this.detailsContainer.classList.remove('hidden')
        this.contentContainer.scrollTop = 0
        this.onShow()
    }

    // eslint-disable-next-line class-methods-use-this
    protected onShow() {
        return
    }

    hide() {
        this.detailsContainer.classList.add('hidden')
        this.onHide()
    }

    // eslint-disable-next-line class-methods-use-this
    protected onHide() {
        return
    }

    private setupEventHandlers() {
        setElementOnClick(this.backButton, () => {
            this.hide()
        })

        setElementOnClick(this.playButton, () => {
            void this.handlePlayButtonClick()
        })
    }

    private async handlePlayButtonClick() {
        const matchType = this.currentMatchType
        if(matchType == undefined) return

        if(Object.values(BotsMatchType).includes(matchType as BotsMatchType)) {
            startBotGame(matchType as BotsMatchType)
            return
        }

        await this.onEnterMatchmaking(matchType as MatchmakingMatchTypes)
    }

    private populateDetails(matchType: MatchType) {
        const modeDetails = UIModeSelectorDetailsController.modeDetails
        const detailsData = modeDetails.get(matchType)
        if(detailsData == undefined) return

        setElementInnerText(this.titleElement, detailsData.title)

        setElementInnerHTML(this.iconContainer, {key: 'strings:colonistCommon.utils.emptyString'})
        if(matchType == MatchmakingMatchTypes.UnrankedShuffle && ClientUserState.userState?.shuffleQueueState != undefined) {
            const shuffleState = ClientUserState.userState.shuffleQueueState
            const stackedIcon = createShuffleQueueIconStack(shuffleState.queueData.mapId, shuffleState.queueData.modeId)
            if(stackedIcon != undefined) this.iconContainer.appendChild(stackedIcon)
        } else {
            createImgIn(this.iconContainer, detailsData.icon, 'mm-details-icon')
        }

        if(detailsData.time != undefined) setElementInnerText(this.timeElement, {key: 'strings:colonistCommon.utils.#buffer', options: {value: detailsData.time.toString()}})
        if(detailsData.playerCount != undefined) setElementInnerText(this.playerCountElement, {key: 'strings:colonistCommon.utils.#buffer', options: {value: detailsData.playerCount.toString()}})

        const canShowInfo = !(matchType == MatchmakingMatchTypes.UnrankedShuffle && ClientUserState.userState?.shuffleQueueState == undefined)
        this.infoElement.classList.toggle('hidden', !canShowInfo)

        setElementInnerText(this.aboutElement, detailsData.about)

        setElementInnerHTML(this.rulesListElement, {key: 'strings:colonistCommon.utils.emptyString'})
        const rulesArray = clientI18n.tArray(detailsData.rules)
        const rulesList = createUnorderedListIn(this.rulesListElement, '', '')
        for(const rule of rulesArray) createListItemIn(rulesList, {key: 'strings:colonistCommon.utils.#buffer', options: {value: rule}}, '', '')

        setElementInnerHTML(this.faqListElement, {key: 'strings:colonistCommon.utils.emptyString'})
        const faqArray = clientI18n.tArray(detailsData.faq) as {question: string, answer: string}[]
        for(const faqItem of faqArray) {
            const itemDiv = createDivIn(this.faqListElement, 'mm-details-faq-item', '')
            const questionText = '<strong>Q:</strong> ' + faqItem.question
            const answerText = '<strong>A:</strong> ' + faqItem.answer
            createParagraphIn(itemDiv, {key: 'strings:colonistCommon.utils.#buffer', options: {value: questionText}}, '')
            createParagraphIn(itemDiv, {key: 'strings:colonistCommon.utils.#buffer', options: {value: answerText}}, '')
        }
    }
}

